import { useEffect, useMemo, useState } from "react";
import { ClockCounterClockwise, UserCircle, Robot, Tag, UsersThree, ArrowsClockwise, Plugs, FunnelSimple, X, SignIn, Stamp, RocketLaunch, Lightning, PaintBrush, UploadSimple, GearSix } from "@phosphor-icons/react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

const ACTIONS = {
  user_login: { label: "signed in", icon: SignIn, cls: "bg-[#00953e]/10 text-[#00953e]" },
  lead_status_changed: { label: "changed a lead status", icon: Tag, cls: "bg-[#0052FF]/10 text-[#0052FF]" },
  ai_action_approved: { label: "approved an AI action", icon: Robot, cls: "bg-success/10 text-[#00953e]" },
  ai_action_rejected: { label: "rejected an AI action", icon: Robot, cls: "bg-red-50 text-red-500" },
  ai_action_recommended: { label: "recommended an AI action", icon: Stamp, cls: "bg-warning/10 text-[#b07600]" },
  creative_signoff: { label: "signed off a creative", icon: Stamp, cls: "bg-[#6366f1]/10 text-[#6366f1]" },
  creative_published: { label: "published a creative to Facebook", icon: RocketLaunch, cls: "bg-success/10 text-[#00953e]" },
  member_created: { label: "added a team member", icon: UsersThree, cls: "bg-[#FF6B35]/10 text-[#c2410c]" },
  member_updated: { label: "updated a team member", icon: UsersThree, cls: "bg-[#FF6B35]/10 text-[#c2410c]" },
  member_removed: { label: "removed a team member", icon: UsersThree, cls: "bg-red-50 text-red-500" },
  meta_synced: { label: "ran a Meta sync", icon: ArrowsClockwise, cls: "bg-[#F3F4F6] text-[#525866]" },
  meta_disconnected: { label: "disconnected Meta", icon: Plugs, cls: "bg-red-50 text-red-500" },
  campaign_action: { label: "changed a campaign", icon: Lightning, cls: "bg-[#0052FF]/10 text-[#0052FF]" },
  creative_design_approved: { label: "approved a creative design", icon: PaintBrush, cls: "bg-[#6366f1]/10 text-[#6366f1]" },
  creative_revision_requested: { label: "asked for a creative revision", icon: ArrowsClockwise, cls: "bg-[#6366f1]/10 text-[#6366f1]" },
  creative_rejected: { label: "rejected a creative job", icon: PaintBrush, cls: "bg-red-50 text-red-500" },
  watch_rule_cancelled: { label: "cancelled a watch rule", icon: Robot, cls: "bg-[#F3F4F6] text-[#525866]" },
  lead_marked: { label: "marked a lead outcome", icon: Tag, cls: "bg-success/10 text-[#00953e]" },
  assessment_uploaded: { label: "imported a sales sheet", icon: UploadSimple, cls: "bg-[#0052FF]/10 text-[#0052FF]" },
  zoho_disconnected: { label: "disconnected Zoho CRM", icon: Plugs, cls: "bg-red-50 text-red-500" },
  agent_memory_deleted: { label: "deleted an agent memory", icon: Robot, cls: "bg-[#F3F4F6] text-[#525866]" },
  settings_changed: { label: "updated settings", icon: GearSix, cls: "bg-[#F3F4F6] text-[#525866]" },
};

const rel = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function ActivityLog() {
  const [items, setItems] = useState(null);
  const [signoffs, setSignoffs] = useState(null);
  const [fActor, setFActor] = useState("all");
  const [fAction, setFAction] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/workspace/activity").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/workspace/signoffs").then(({ data }) => setSignoffs(data)).catch(() => setSignoffs([]));
  }, []);

  const actors = useMemo(() => {
    const m = new Map();
    (items || []).forEach((i) => m.set(i.actor_id, i.actor_name || i.actor_email));
    return [...m.entries()];
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return null;
    const s = q.toLowerCase();
    return items.filter((i) =>
      (fActor === "all" || i.actor_id === fActor) &&
      (fAction === "all" || i.action === fAction) &&
      (!s || (i.target || "").toLowerCase().includes(s) || (i.details || "").toLowerCase().includes(s) || (i.actor_name || "").toLowerCase().includes(s)));
  }, [items, fActor, fAction, q]);

  const active = (fActor !== "all" ? 1 : 0) + (fAction !== "all" ? 1 : 0) + (q ? 1 : 0);

  return (
    <div className="max-w-3xl space-y-5" data-testid="activity-log-page">
      <div>
        <h2 className="font-heading text-2xl font-extrabold tracking-tight text-[#0A0D14]">Activity Log</h2>
        <p className="text-sm text-[#525866] mt-1">Who did what across this workspace — lead status changes, AI decisions, team and connection changes.</p>
      </div>

      <Tabs defaultValue="activity">
        <TabsList className="bg-white border border-[#E5E7EB] rounded-lg p-1">
          <TabsTrigger value="activity" data-testid="tab-activity" className="rounded-md text-xs font-bold"><ClockCounterClockwise size={14} className="mr-1.5" />All Activity</TabsTrigger>
          <TabsTrigger value="signoffs" data-testid="tab-signoffs" className="rounded-md text-xs font-bold"><Stamp size={14} className="mr-1.5" />Sign-off History ({(signoffs || []).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="mt-4 space-y-5">

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex items-center gap-2 flex-wrap" data-testid="activity-filter-bar">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A0D14] mr-1"><FunnelSimple size={15} weight="bold" /> Filters</span>
        <Select value={fActor} onValueChange={setFActor}>
          <SelectTrigger className="w-44 h-9 text-xs font-semibold bg-white" data-testid="activity-filter-actor"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All members</SelectItem>
            {actors.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fAction} onValueChange={setFAction}>
          <SelectTrigger className="w-52 h-9 text-xs font-semibold bg-white" data-testid="activity-filter-action"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity types</SelectItem>
            {Object.entries(ACTIONS).map(([k, a]) => <SelectItem key={k} value={k}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search lead, campaign, member…" data-testid="activity-search-input" className="h-9 w-56 text-xs bg-white" />
        {active > 0 && (
          <button onClick={() => { setFActor("all"); setFAction("all"); setQ(""); }} data-testid="activity-filter-clear"
            className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[11px] font-bold text-[#525866] px-3 py-1.5 hover:bg-[#e7e9ee]">
            <X size={11} weight="bold" /> Clear ({active})
          </button>
        )}
      </div>

      {!filtered ? (
        <div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#c9cdd6] rounded-lg p-10 text-center" data-testid="activity-empty">
          <ClockCounterClockwise size={22} weight="duotone" className="mx-auto text-[#0052FF]" />
          <p className="text-xs text-[#8a90a0] mt-2">{items?.length === 0 ? "No activity recorded yet. Actions like lead status changes and AI approvals will appear here." : "Nothing matches these filters."}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-lg divide-y divide-[#E5E7EB]/60">
          {filtered.map((i) => {
            const meta = ACTIONS[i.action] || { label: i.action, icon: UserCircle, cls: "bg-[#F3F4F6] text-[#525866]" };
            return (
              <div key={i.id} className="flex items-start gap-3 px-4 py-3" data-testid={`activity-row-${i.id}`}>
                <span className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center ${meta.cls}`}><meta.icon size={15} weight="duotone" /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[12.5px] text-[#0A0D14] leading-snug">
                    <b>{i.actor_name || i.actor_email}</b>{i.actor_role === "owner" && <span className="text-[9px] font-bold text-[#c2410c] ml-1">OWNER</span>} {meta.label}
                    {i.target && <> — <span className="font-semibold">{i.target}</span></>}
                  </div>
                  {i.details && <div className="text-[11px] text-[#8a90a0] mt-0.5">{i.details}</div>}
                </div>
                <span className="shrink-0 text-right">
                  <span className="block text-[11px] font-bold text-[#525866]">{rel(i.created_at)}</span>
                  <span className="block text-[9.5px] text-[#c3c7d1]">{new Date(i.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </span>
              </div>
            );
          })}
        </div>
      )}
        </TabsContent>

        <TabsContent value="signoffs" className="mt-4">
          {!signoffs ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}</div>
          ) : signoffs.length === 0 ? (
            <div className="bg-white border border-dashed border-[#c9cdd6] rounded-lg p-10 text-center" data-testid="signoffs-empty">
              <Stamp size={22} weight="duotone" className="mx-auto text-[#0052FF]" />
              <p className="text-xs text-[#8a90a0] mt-2">No sign-offs requested yet. When a team member signs off a creative or recommends an AI action, it appears here with their name and date.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-[#E5E7EB] bg-white overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]" data-testid="signoffs-table">
                <thead>
                  <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[11px] uppercase tracking-wide text-[#8a90a0]">
                    <th className="px-4 py-3 font-bold">Requested by</th>
                    <th className="px-4 py-3 font-bold">Item</th>
                    <th className="px-4 py-3 font-bold">Type</th>
                    <th className="px-4 py-3 font-bold">Requested on</th>
                    <th className="px-4 py-3 font-bold">Outcome</th>
                  </tr>
                </thead>
                <tbody>
                  {signoffs.map((s) => (
                    <tr key={s.id} className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFB]" data-testid={`signoff-row-${s.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-bold text-xs text-[#0A0D14]">{s.actor_name || s.actor_email}</div>
                        <div className="text-[10px] text-[#8a90a0]">{s.actor_email}</div>
                      </td>
                      <td className="px-4 py-3"><div className="max-w-[220px] text-xs font-semibold text-[#0A0D14] truncate" title={s.title}>{s.title}</div>
                        {s.detail && <div className="max-w-[220px] text-[10px] text-[#8a90a0] truncate" title={s.detail}>{s.detail}</div>}</td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase ${s.kind === "creative" ? "bg-[#6366f1]/10 text-[#6366f1]" : "bg-ai/10 text-ai"}`}>
                          {s.kind === "creative" ? "Creative" : "AI Action"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#525866] whitespace-nowrap">{new Date(s.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td className="px-4 py-3">
                        {s.status === "pending" ? (
                          <span className="rounded-full bg-warning/10 text-[#b07600] px-2 py-0.5 text-[10px] font-bold uppercase">Pending</span>
                        ) : (
                          <div>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${s.resolution === "rejected" ? "bg-red-50 text-red-500" : "bg-success/10 text-[#00953e]"}`}>{s.resolution}</span>
                            <div className="text-[10px] text-[#8a90a0] mt-1">by {s.resolved_by} · {s.resolved_at ? new Date(s.resolved_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
